import { inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, Observable, of, switchMap, take, timeout } from 'rxjs';

import {
  GroupsSelectors,
  GroupsStoreActions,
  UserStoreActions,
  UserStoreSelectors,
} from '@hosty-app/app-store';
import { isAuthError } from '@hosty-app/utils';

import { AuthorizationService } from '../authorization-service/authorization.service';

/** Where the user lands when the session is fine but the server is unreachable. */
export const CONNECTION_PROBLEM_ROUTE = '/connection-problem';

/**
 * Nothing in the guard depends on the subscription result — it only waits for it
 * so downstream screens have it. If the action never arrives, proceed rather
 * than wedging the router and leaving the user on a blank screen.
 */
const SUBSCRIPTION_WAIT_MS = 15_000;

export const authGuard = (): Observable<boolean | UrlTree> | UrlTree => {
  const authService = inject(AuthorizationService);
  const router = inject(Router);
  const actions$ = inject(Actions);
  const store$ = inject(Store);
  if (!authService.jwtToken || !authService.refreshToken) {
    return router.createUrlTree(['/login']);
  }

  store$.dispatch(UserStoreActions.getProfile());
  store$.dispatch(GroupsStoreActions.get());

  return combineLatest([
    store$.select(UserStoreSelectors.selectProfileLoading).pipe(filter((l) => !l)),
    store$.select(GroupsSelectors.selectLoading).pipe(filter((l) => !l)),
  ]).pipe(
    switchMap((): Observable<unknown> => {
      setTimeout(() => store$.dispatch(UserStoreActions.getUserSubscription()));
      return actions$.pipe(
        ofType(
          UserStoreActions.getUserSubscriptionSuccess,
          UserStoreActions.getUserSubscriptionFailure,
        ),
        take(1),
        timeout({ first: SUBSCRIPTION_WAIT_MS, with: () => of(null) }),
      );
    }),
    switchMap(() =>
      combineLatest([
        store$.select(UserStoreSelectors.selectUserProfile),
        store$.select(UserStoreSelectors.selectProfileError),
      ]),
    ),
    map(([profile, error]) => {
      if (profile) {
        return true;
      }

      // The server rejected the session — signing in again is the right answer.
      // (RefreshTokenInterceptor has already cleared the tokens by this point.)
      if (isAuthError(error)) {
        return router.createUrlTree(['/login']);
      }

      // We could not reach the server. The credentials are untouched and
      // probably still valid, so do NOT send the user to the sign-in screen —
      // that reads as a silent logout and makes them re-enter their password
      // for a problem a retry would fix.
      return router.createUrlTree([CONNECTION_PROBLEM_ROUTE]);
    }),
  );
};
